import { useEffect, useState } from 'react';
import { Store as StoreIcon } from 'lucide-react';

import { getAsset } from '@/client/utils/assets';
import { cn } from '@/lib/utils';

/**
 * URL for a store's `photo` / `banner`. The column is an asset reference
 * (`{filename, path, path_root, uuid_uc}`), occasionally a plain URL. The
 * preset must be one the asset endpoint knows — an unknown one is a 400, which
 * is what left every store image broken.
 */
export function storeMediaUrl(value: unknown, preset: AssetProfile): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value.startsWith('http') ? value : undefined;
  if (typeof value !== 'object') return undefined;
  // A removed image leaves the reference behind with every field null;
  // getAsset returns undefined for that.
  return getAsset(value as never, preset);
}

export const storeLogoUrl = (store: { photo?: unknown }) => storeMediaUrl(store.photo, 'thumbnail');
export const storeBannerUrl = (store: { banner?: unknown }) => storeMediaUrl(store.banner, 'medium');

/** A store image that falls back to the store icon when missing or unloadable. */
export const StoreImage: React.FC<{
  src?: string;
  alt?: string;
  className?: string;
  iconClassName?: string;
}> = ({ src, alt = '', className, iconClassName = 'h-5 w-5 text-gray-400' }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) {
    return (
      <span className={cn('flex items-center justify-center', className)}>
        <StoreIcon className={iconClassName} aria-hidden="true" />
      </span>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={cn('object-cover', className)} />;
};
